import React from 'react';

import { Box } from '@mui/material';

import logoSource from './iidf-logo.png';

import type { IidfLogoProperties } from './IidfLogo.types';

/**
 * Логотип ФРИИ.
 *
 * ─── Почему картинка лежит в пакете, а не в `public/` приложения ────────────
 *
 * До 27.09.2026 (AF-580) компонент жил в `apps/analyst`, а `AuthPageTitle`
 * рисовал тот же логотип сырым `<img>` — два способа нарисовать одно, и
 * `check:duplicates` их не видел: его признак пофайловый и по относительному
 * пути, а пути были разные.
 *
 * Файл побайтово одинаков во всех четырёх приложениях (md5
 * 1d034c3c73ef289a690d84c282063c41), то есть попадает под первый пункт правила
 * подъёма из `packages/README.md` — «побайтово одинаковые файлы поднимаются
 * как есть».
 *
 * Ассет импортируется, а не берётся по фиксированному адресу. Так компонент
 * самодостаточен: приложению не нужно ничего класть в `public/`, а сборщик
 * потребителя сам эмитит копию с хешем в содержимом. Побочно это чинит
 * инвалидацию кеша — у неизменного `/iidf-logo.png` её не было вовсе.
 *
 * ─── Отвергнуто: оставить `src='/iidf-logo.png'` ────────────────────────────
 *
 * Это договорённость на словах: пакет молча требует, чтобы каждое приложение
 * положило файл по этому пути, и нарушение проявится пустой картинкой в
 * проде. Репозиторий такие требования заменяет структурными — см. каталог
 * версий в `pnpm-workspace.yaml`.
 *
 * ─── Следствие, которое пришлось убрать ─────────────────────────────────────
 *
 * У analyst и applications в `index.html` стоял
 * `<link rel="preload" as="image" href="/iidf-logo.png">`. С хешированным
 * адресом он грел бы файл, которого никто не запросит, — то есть стал бы
 * чистой тратой. Ссылки сняты. Потеря невелика и неравномерной раньше не
 * считалась: логотип весит 4944 байта, а у deals и viewer этого preload не
 * было вовсе.
 */
export const IidfLogo: React.FC<IidfLogoProperties> = ({
  height = 32,
  sx,
  dataTest = 'iidf_logo',
}) => (
  <Box
    component='img'
    src={logoSource}
    alt='ФРИИ'
    data-testid={dataTest}
    sx={{
      height,
      width: 'auto',
      objectFit: 'contain',
      display: 'block',
      ...sx,
    }}
  />
);
